import * as React from 'react';
import { forwardRef } from 'react';
import PhoneInputComponent from 'react-phone-number-input';
const { formatPhoneNumberIntl } = require('react-phone-number-input');

import { TextFieldProps } from '@mui/material/TextField';

import Input from '../Input/Input';
import { useTheme } from '../theme';
import cn from 'classnames';
import { normalizePhoneNumber } from './utils';

interface Props {
	className?: string;
	value?: string;
	textFieldsProps?: TextFieldProps;
	displayInitialValueAsLocalNumber?: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
	renderInput?: (props: React.ComponentProps<typeof Input>) => React.ReactElement;
}

interface PhoneNumberInputComponentProps {
	className?: string;
	value: string;
	onChange: (value: string) => void;
	onFocus: React.FocusEventHandler<HTMLInputElement>;
	onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
	disabled?: boolean;
	autoFocus?: boolean;
	renderInput?: Props['renderInput'];
}

const Context = React.createContext<TextFieldProps>({});

const InputWrapper: React.FC<PhoneNumberInputComponentProps> = forwardRef(({ onFocus, onKeyDown, ...props }, ref) => {
	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		props.onChange(normalizePhoneNumber(e.target.value));
	};

	const parsedPhone = formatPhoneNumberIntl(props.value) || formatPhoneNumberIntl(`+${props.value}`);

	const textFieldsProps = React.useContext<TextFieldProps>(Context);
	const { value, ...InputProps } = textFieldsProps?.InputProps ?? {};

	const css = useTheme('PhoneInput').PhoneInput;

	if (props.renderInput) {
		return props.renderInput({
			absoluteHintPosition: true,
			className: cn(css.input, props.className),
			inputClassName: textFieldsProps.className,
			variant: textFieldsProps.variant,
			TextFieldProps: {
				onFocus,
				onKeyDown,
				onChange,

				// Do not spread `context.textFieldsProps` there,
				// or `react-phone-number-input` won't work.
				name: textFieldsProps.name,
				onBlur: textFieldsProps.onBlur,
				label: textFieldsProps.label,
				error: textFieldsProps.error,
				helperText: textFieldsProps.helperText,
				disabled: textFieldsProps.disabled || props.disabled,
				placeholder: textFieldsProps.placeholder,
				type: 'tel',
				InputProps,
				className: textFieldsProps.className,
				InputLabelProps: textFieldsProps.InputLabelProps
			},
			disabled: props.disabled,
			value: parsedPhone ? parsedPhone : props.value,
			tabIndex: textFieldsProps.tabIndex,
			inputMode: 'tel',
			autoFocus: props.autoFocus,
			inputProps: textFieldsProps.inputProps
		});
	}

	return (
		<Input
			absoluteHintPosition={true}
			className={cn(css.input, props.className)}
			inputClassName={textFieldsProps.className}
			variant={textFieldsProps.variant}
			TextFieldProps={{
				onFocus,
				onKeyDown,
				onChange,

				// Do not spread `context.textFieldsProps` there,
				// or `react-phone-number-input` won't work.
				name: textFieldsProps.name,
				onBlur: textFieldsProps.onBlur,
				label: textFieldsProps.label,
				error: textFieldsProps.error,
				helperText: textFieldsProps.helperText,
				disabled: textFieldsProps.disabled || props.disabled,
				placeholder: textFieldsProps.placeholder,
				type: 'tel',
				InputProps,
				className: textFieldsProps.className,
				InputLabelProps: textFieldsProps.InputLabelProps
			}}
			disabled={props.disabled}
			value={parsedPhone ? parsedPhone : props.value}
			tabIndex={textFieldsProps.tabIndex}
			inputMode="tel"
			autoFocus={props.autoFocus}
			inputProps={textFieldsProps.inputProps}
		/>
	);
});

const PhoneInput: React.FunctionComponent<Props> = ({
	className,
	value,
	textFieldsProps = {},
	displayInitialValueAsLocalNumber = true,
	disabled,
	autoFocus,
	renderInput
}) => {
	return (
		<Context.Provider value={textFieldsProps}>
			<PhoneInputComponent
				className={className}
				onChange={textFieldsProps.onChange}
				onFocus={textFieldsProps.onFocus}
				onBlur={textFieldsProps.onBlur}
				showCountrySelect={false}
				limitMaxLength={true}
				displayInitialValueAsLocalNumber={displayInitialValueAsLocalNumber}
				value={value || ''}
				inputComponent={InputWrapper}
				disabled={disabled}
				autoFocus={autoFocus}
				renderInput={renderInput}
			/>
		</Context.Provider>
	);
};

export default PhoneInput;
